import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Clock, CreditCard, IndianRupee, Receipt } from "lucide-react";
import { ReportsService } from "../../../services/reports.service";
import { ReportCard, ReportPanel } from "./components/ReportCards";
import DataTable from "./components/DataTable";
import TrendBars from "./components/TrendBars";
import DriverDetailModal from "./components/DriverDetailModal";
import { money, n } from "./components/reportFormat";

export default function MoneyReports({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [driverDetailOpen, setDriverDetailOpen] = useState(false);
  const [driverDetail, setDriverDetail] = useState(null);
  const [driverDetailLoading, setDriverDetailLoading] = useState(false);
  const [driverDetailError, setDriverDetailError] = useState("");
  const [selectedDriverRow, setSelectedDriverRow] = useState(null);
  const [driverOrdersPage, setDriverOrdersPage] = useState(1);
  const driverOrdersLimit = 10;

  useEffect(() => {
    let alive = true;

    setLoading(true);

    ReportsService.driverMoney(filters)
      .then((r) => alive && setData(r.data))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [filters]);

  const fetchDriverDetail = useCallback(async (row, page = 1) => {
    const partnerId = row?.partner_id || row?.partnerId || row?.id;
    if (!partnerId) return;

    setSelectedDriverRow(row);
    setDriverOrdersPage(page);
    setDriverDetailOpen(true);
    setDriverDetailError("");
    setDriverDetailLoading(true);

    try {
      const res = await ReportsService.driverDetail(partnerId, {
        ordersPage: page,
        ordersLimit: driverOrdersLimit,
      });
      setDriverDetail(res?.data || res || null);
    } catch (err) {
      setDriverDetailError(err?.message || "Driver detail API failed");
    } finally {
      setDriverDetailLoading(false);
    }
  }, []);

  const openDriverDetail = useCallback((row) => {
    setDriverDetail(null);
    fetchDriverDetail(row, 1);
  }, [fetchDriverDetail]);

  const changeDriverOrdersPage = useCallback((page) => {
    if (!selectedDriverRow) return;
    fetchDriverDetail(selectedDriverRow, page);
  }, [fetchDriverDetail, selectedDriverRow]);

  const rawRows = data?.rows || [];

  const rows = rawRows.map((row) => {
    const driverName =
      row.driver_name ||
      row.driverName ||
      row.name ||
      row.partner_name ||
      row.partnerName ||
      row.partner_code ||
      row.partnerCode ||
      "Driver";

    return {
      ...row,
      driver: driverName,
      phone: row.driver_phone || row.phone || "-",
      wallet_balance: row.available_balance ?? row.availableBalance ?? 0,
      lifetime_earnings: row.lifetime_earnings ?? row.lifetimeEarnings ?? row.earned_amount ?? 0,
      lifetime_withdrawn: row.lifetime_withdrawn ?? row.lifetimeWithdrawn ?? row.paid_amount ?? 0,
      request_count: row.request_count ?? row.requestCount ?? 0,
      requested_amount: row.requested_amount ?? row.requestedAmount ?? 0,
      paid_amount: row.paid_amount ?? row.paidAmount ?? row.lifetime_withdrawn ?? 0,
      pending_amount: row.pending_amount ?? row.pendingAmount ?? 0,
      last_request_at: row.last_request_at || row.lastRequestAt || row.last_transaction_at || row.lastTransactionAt || row.last_activity_at || row.lastActivityAt,
    };
  });

  const columns = [
    "driver",
    "phone",
    "wallet_balance",
    "lifetime_earnings",
    "lifetime_withdrawn",
    "request_count",
    "requested_amount",
    "paid_amount",
    "pending_amount",
    "last_request_at",
  ];

  const apiStats = data?.stats || {};
  const stats = useMemo(() => ({
    earned: Number(apiStats.earned ?? rows.reduce((s, r) => s + Number(r.lifetime_earnings || r.earned_amount || r.estimated_income || 0), 0)),
    paid: Number(apiStats.paid ?? rows.reduce((s, r) => s + Number(r.lifetime_withdrawn || r.paid_amount || 0), 0)),
    pending: Number(apiStats.pending ?? rows.reduce((s, r) => s + Number(r.pending_amount || 0), 0)),
    count: Number(apiStats.transactions ?? rows.reduce((s, r) => s + Number(r.transaction_count || r.request_count || r.delivered_tasks || 0), 0)),
  }), [apiStats.earned, apiStats.paid, apiStats.pending, apiStats.transactions, rows]);

  const trendRows = rows.map((r, i) => ({
    bucket: r.last_request_at || r.last_transaction_at || r.last_activity_at || new Date(Date.now() + i * 86400000).toISOString(),
    earned_amount: Number(r.lifetime_earnings || r.earned_amount || r.estimated_income || r.requested_amount || 0),
  }));

  if (loading) {
    return (
      <div className="py-14 text-center font-bold text-[#d86a78]">
        Loading driver money report...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <ReportCard icon={IndianRupee} title="Earned" value={money(stats.earned)} />
        <ReportCard icon={CreditCard} title="Paid" value={money(stats.paid)} />
        <ReportCard icon={Clock} title="Pending" value={money(stats.pending)} />
        <ReportCard icon={Receipt} title="Transactions" value={n(stats.count)} />
      </div>

      <TrendBars
        rows={trendRows}
        field="earned_amount"
        label="Driver money trend"
        subtitle="Graph is created from money rows when API trend is not available"
      />

      <ReportPanel>
        <DataTable rows={rows} columns={columns} onRowClick={openDriverDetail} />
      </ReportPanel>
      <DriverDetailModal
        open={driverDetailOpen}
        loading={driverDetailLoading}
        error={driverDetailError}
        detail={driverDetail}
        ordersPage={driverOrdersPage}
        ordersLimit={driverOrdersLimit}
        onOrdersPageChange={changeDriverOrdersPage}
        onClose={() => {
          setDriverDetailOpen(false);
          setDriverDetail(null);
          setDriverDetailError("");
          setSelectedDriverRow(null);
          setDriverOrdersPage(1);
        }}
      />
    </div>
  );
}